import React from "react";

type Props = {
  size?: number;
  waterColor?: string;
  waterGradient?: string[];
  duration?: number;
  backdropColor?: string;
  backdropOpacity?: number;
  logoPathD?: string;
  percent?: number;
};

const DEFAULT_PATH =
  "M98.96,15.76c-43.09,0-78.03,34.93-78.03,78.03s34.93,78.03,78.03,78.03,78.03-34.93,78.03-78.03S142.05,15.76,98.96,15.76ZM108.11,155.38c-3.62.53-5.44-2.12-4.35-6.05,1.1-3.97,3.08-7.58,4.3-11.47,1.16-3.69,2.01-7.49,2.54-11.32.43-3.12.59-6.28.43-9.42-.04-.85-1.18-9.81-1.43-9.74-7.44,2.31-16.46.39-21.71-5.53-8.41-9.47-5.29-23.42,1.7-32.73,3.73-4.97,8.07-9.45,11.48-14.66,3.27-5,5.87-10.44,7.68-16.14.22-.69.45-1.41.72-2.11.81-2.11,3.09-3.25,5.28-2.68,26.74,6.99,46.47,31.32,46.47,60.25,0,31.28-23.07,57.17-53.12,61.6Z";

export default function ThermoWaterLoading({
  size = 200,
  waterColor = "#feb699",
  waterGradient,
  duration = 2.5,
  backdropColor = "#FFFFFF",
  backdropOpacity = 0,
  logoPathD = DEFAULT_PATH,
  percent = undefined,
}: Props) {
  const id = React.useId();
  const waveSpeed = duration;
  const waveBackSpeed = duration * 1.6;
  const [fillPercent, setFillPercent] = React.useState(0);

  const gradientId = `waterGrad-${id}`;
  const maskId = `mask-${id}`;
  const waveId = `wave-${id}`;

  const waterTranslateY = React.useMemo(() => {
    return 100 - Math.max(0, Math.min(100, fillPercent));
  }, [fillPercent]);

  React.useEffect(() => {
    setFillPercent(percent !== undefined ? percent : 80);

    return () => setFillPercent(0);
  }, [percent, setFillPercent]);

  return (
    <div
      style={{
        width: size,
        height: size,
        position: "relative",
        display: "inline-block",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: backdropColor,
          opacity: backdropOpacity,
          borderRadius: 12,
        }}
      />
      <svg
        viewBox="0 0 197.92 187.57"
        width={size}
        height={size}
        style={{ position: "relative", display: "block" }}
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Thermogram loading"
      >
        <defs>
          <mask id={maskId}>
            <rect width="100%" height="100%" fill="#000" />
            <path d={logoPathD} fill="#fff" />
          </mask>

          <path
            id={waveId}
            d="M0 0 Q 25 10 50 0 T100 0 T150 0 T200 0 V200 H0 Z"
          />

          {waterGradient && waterGradient.length > 1 ? (
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              {waterGradient.map((c, i) => (
                <stop
                  key={i}
                  offset={`${(i / (waterGradient.length - 1)) * 100}%`}
                  stopColor={c}
                />
              ))}
            </linearGradient>
          ) : null}
        </defs>

        <g mask={`url(#${maskId})`}>
          <rect x="0" y="0" width="197.92" height="187.57" fill="#FFFFFF77" />

          {/* กล่องน้ำเลื่อนขึ้น/ลงตามเปอร์เซ็นต์ */}
          <g
            style={{
              transform: `translateY(${waterTranslateY}%)`,
              transition: "transform 0.6s ease-out",
            }}
          >
            <rect
              x="0"
              y="0"
              width="197.92"
              height="187.57"
              fill={
                waterGradient && waterGradient.length > 1
                  ? `url(#${gradientId})`
                  : waterColor
              }
              opacity={0.75}
            />

            <g
              fill={
                waterGradient && waterGradient.length > 1
                  ? `url(#${gradientId})`
                  : waterColor
              }
              opacity={0.95}
              style={{ animation: `flow-${id} ${waveSpeed}s linear infinite` }}
            >
              <use href={`#${waveId}`} />
              <use href={`#${waveId}`} x="200" />
            </g>

            <g
              fill={
                waterGradient && waterGradient.length > 1
                  ? `url(#${gradientId})`
                  : waterColor
              }
              opacity={0.6}
              transform="translate(0,6)"
              style={{
                animation: `flowBack-${id} ${waveBackSpeed}s linear infinite`,
              }}
            >
              <use href={`#${waveId}`} />
              <use href={`#${waveId}`} x="200" />
            </g>
          </g>
        </g>

        <style>
          {`
            @keyframes flow-${id} {
              0% { transform: translateX(0); }
              100% { transform: translateX(-200px); }
            }
            @keyframes flowBack-${id} {
              0% { transform: translateX(0); }
              100% { transform: translateX(200px); }
            }
          `}
        </style>
      </svg>
    </div>
  );
}
